import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { GoogleDriveFolderPicker } from '@/components/integrations/google-drive-folder-picker'
import { googleIntegrationService } from '@/services/google-integration'
import { knowledgeBaseService } from '@/services/knowledge-base'
import {
  SYNC_FREQUENCY_OPTIONS,
  FILE_TYPE_OPTIONS,
  DEFAULT_FILE_TYPES,
  formatFileSize,
  formatSyncDate,
  getSyncStatusLabel,
  getSyncStatusColor,
  type GoogleDriveFileItem,
  type SyncFrequency,
} from '@/schemas/google-drive-sync'
import {
  Folder,
  RefreshCw,
  Loader2,
  Cloud,
  FileText,
  Clock,
  Filter,
  FolderOpen,
  AlertCircle,
} from 'lucide-react'

interface GoogleDriveSyncConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  projectId: string
  knowledgeBaseId: string
  knowledgeBaseName: string
  googleEmail?: string
  onSyncConfigured?: () => void
  onError?: (message: string) => void
}

export function GoogleDriveSyncConfigDialog({
  open,
  onOpenChange,
  organizationId,
  projectId,
  knowledgeBaseId,
  knowledgeBaseName,
  googleEmail,
  onSyncConfigured,
  onError,
}: GoogleDriveSyncConfigDialogProps) {
  // State
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)

  // Config state
  const [selectedFolder, setSelectedFolder] = useState<{
    id: string
    name: string
    path: string
  } | null>(null)
  const [frequency, setFrequency] = useState<SyncFrequency>('manual')
  const [fileTypes, setFileTypes] = useState<string[]>(DEFAULT_FILE_TYPES)
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<string>('idle')

  // Preview state
  const [previewFiles, setPreviewFiles] = useState<GoogleDriveFileItem[]>([])
  const [previewCount, setPreviewCount] = useState(0)

  // Load existing config
  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true)
      const config = await knowledgeBaseService.getGoogleDriveSyncConfig(
        projectId,
        knowledgeBaseId
      )

      if (config.enabled && config.folder_id) {
        setSelectedFolder({
          id: config.folder_id,
          name: config.folder_name || '',
          path: config.folder_path || '',
        })
      }
      setFrequency((config.frequency as SyncFrequency) || 'manual')
      setFileTypes(config.file_types.length > 0 ? config.file_types : DEFAULT_FILE_TYPES)
      setLastSyncAt(config.last_sync_at)
      setSyncStatus(config.sync_status)
    } catch (err) {
      console.error('Failed to load sync config:', err)
      // Not an error - config might not exist yet
    } finally {
      setIsLoading(false)
    }
  }, [projectId, knowledgeBaseId])

  useEffect(() => {
    if (open) {
      loadConfig()
    }
  }, [open, loadConfig])

  // Load preview when folder or file types change
  const loadPreview = useCallback(async () => {
    if (!selectedFolder?.id) {
      setPreviewFiles([])
      setPreviewCount(0)
      return
    }

    try {
      setIsLoadingPreview(true)
      const response = await googleIntegrationService.previewFolderFiles(
        organizationId,
        selectedFolder.id,
        fileTypes
      )
      setPreviewFiles(response.files.slice(0, 10)) // Show first 10
      setPreviewCount(response.total_count)
    } catch (err) {
      console.error('Failed to load preview:', err)
      setPreviewFiles([])
      setPreviewCount(0)
    } finally {
      setIsLoadingPreview(false)
    }
  }, [organizationId, selectedFolder?.id, fileTypes])

  useEffect(() => {
    if (open && selectedFolder?.id) {
      loadPreview()
    }
  }, [open, selectedFolder?.id, fileTypes, loadPreview])

  const handleFolderSelect = (folder: { id: string; name: string; path: string }) => {
    setSelectedFolder(folder)
  }

  const handleFileTypeToggle = (type: string) => {
    setFileTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  const handleSave = async () => {
    setInternalError(null)

    if (!selectedFolder) {
      setInternalError('Selecione uma pasta do Google Drive')
      return
    }

    if (fileTypes.length === 0) {
      setInternalError('Selecione pelo menos um tipo de arquivo')
      return
    }

    try {
      setIsSaving(true)
      await knowledgeBaseService.configureGoogleDriveSync(projectId, knowledgeBaseId, {
        enabled: true,
        folder_id: selectedFolder.id,
        folder_name: selectedFolder.name,
        folder_path: selectedFolder.path,
        frequency,
        file_types: fileTypes,
      })

      onSyncConfigured?.()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save config:', err)
      const message = 'Falha ao salvar configuracao'
      setInternalError(message)
      onError?.(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSyncNow = async () => {
    setInternalError(null)

    if (!selectedFolder) {
      setInternalError('Configure uma pasta antes de sincronizar')
      return
    }

    try {
      setIsSyncing(true)

      // Save config first
      await knowledgeBaseService.configureGoogleDriveSync(projectId, knowledgeBaseId, {
        enabled: true,
        folder_id: selectedFolder.id,
        folder_name: selectedFolder.name,
        folder_path: selectedFolder.path,
        frequency,
        file_types: fileTypes,
      })

      // Trigger sync
      const result = await knowledgeBaseService.triggerGoogleDriveSync(
        projectId,
        knowledgeBaseId
      )

      setSyncStatus(result.status)
      onSyncConfigured?.()
    } catch (err) {
      console.error('Failed to trigger sync:', err)
      const message = 'Falha ao iniciar sincronizacao'
      setInternalError(message)
      onError?.(message)
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              Configurar Sync Google Drive
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure a sincronizacao de arquivos do Google Drive para{' '}
              <span className="text-gray-200 font-medium">{knowledgeBaseName}</span>
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Error Alert */}
              {internalError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-400">{internalError}</span>
                </div>
              )}

              {/* Google Account */}
              {googleEmail && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <Cloud className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-emerald-400">
                    Conectado como {googleEmail}
                  </span>
                </div>
              )}

              {/* Folder Selection */}
              <div className="space-y-2">
                <Label className="text-gray-200 flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  Pasta do Google Drive
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-md border ${
                      selectedFolder
                        ? 'bg-gray-800 border-gray-600'
                        : 'bg-gray-800/50 border-gray-700 border-dashed'
                    }`}
                  >
                    {selectedFolder ? (
                      <>
                        <FolderOpen className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm text-gray-200 truncate">
                          {selectedFolder.path || selectedFolder.name}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">
                        Nenhuma pasta selecionada
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setFolderPickerOpen(true)}
                    className="border-gray-600 hover:bg-gray-800"
                  >
                    {selectedFolder ? 'Alterar' : 'Selecionar'}
                  </Button>
                </div>
              </div>

              {/* Sync Frequency */}
              <div className="space-y-2">
                <Label className="text-gray-200 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Frequencia de Sincronizacao
                </Label>
                <Select value={frequency} onValueChange={(v) => setFrequency(v as SyncFrequency)}>
                  <SelectTrigger className="bg-gray-800 border-gray-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {SYNC_FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* File Types */}
              <div className="space-y-2">
                <Label className="text-gray-200 flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Tipos de Arquivo
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {FILE_TYPE_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        fileTypes.includes(option.value)
                          ? 'bg-blue-500/10 border-blue-500/50'
                          : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <Checkbox
                        checked={fileTypes.includes(option.value)}
                        onCheckedChange={() => handleFileTypeToggle(option.value)}
                        className="border-gray-500"
                      />
                      <span className="text-sm text-gray-300">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* File Preview */}
              {selectedFolder && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-200 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Preview de Arquivos
                      {previewCount > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {previewCount} arquivo{previewCount !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadPreview}
                      disabled={isLoadingPreview}
                      className="text-gray-400"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${isLoadingPreview ? 'animate-spin' : ''}`}
                      />
                    </Button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto border border-gray-700 rounded-md divide-y divide-gray-700/50">
                    {isLoadingPreview ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                      </div>
                    ) : previewFiles.length === 0 ? (
                      <div className="py-8 text-center">
                        <FileText className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">
                          Nenhum arquivo encontrado com os filtros selecionados
                        </p>
                      </div>
                    ) : (
                      <>
                        {previewFiles.map((file) => (
                          <div
                            key={file.id}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-800/50"
                          >
                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="flex-1 text-sm text-gray-300 truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                        ))}
                        {previewCount > 10 && (
                          <div className="px-3 py-2 text-center text-xs text-gray-500">
                            E mais {previewCount - 10} arquivos...
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Sync Status */}
              {lastSyncAt && (
                <div className="flex items-center justify-between px-3 py-2 bg-gray-800/30 rounded-md">
                  <span className="text-sm text-gray-400">Ultima sincronizacao:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-300">
                      {formatSyncDate(lastSyncAt)}
                    </span>
                    <Badge variant={getSyncStatusColor(syncStatus)}>
                      {getSyncStatusLabel(syncStatus)}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-400"
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={handleSyncNow}
              disabled={isSyncing || isSaving || !selectedFolder}
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sincronizar Agora
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || isSyncing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Salvar Configuracao
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GoogleDriveFolderPicker
        open={folderPickerOpen}
        onOpenChange={setFolderPickerOpen}
        organizationId={organizationId}
        onSelect={handleFolderSelect}
        initialFolderId={selectedFolder?.id}
      />
    </>
  )
}
